
import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent,
  CardFooter,
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2, Plus, X, Check, Mail } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SocialMedia, FAQ, TeamMember, ContactData } from '@/types/contact';

interface Contact {
  id: string;
  name: string;
  email: string;
  message: string;
  created_at: string;
}

export interface ContactAdminProps {
  conferenceId: string;
}

const ContactAdmin: React.FC<ContactAdminProps> = ({ conferenceId }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contact_details')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContacts(data as any[] || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: "Error",
        description: "Failed to load contact data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', message: '' });
    setIsEditing(false);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        // Implement update logic if needed
        toast({
          title: "Error",
          description: "Editing not supported for contacts.",
          variant: "destructive",
        });
        return;
      } else {
        // Adding a new contact from admin is not allowed
        toast({
          title: "Error",
          description: "Adding new contacts not supported from admin.",
          variant: "destructive",
        });
        return;
      }
      
      resetForm();
      fetchContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast({
        title: "Error",
        description: "Failed to save contact data",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (contact: Contact) => {
    setFormData({
      name: contact.name,
      email: contact.email,
      message: contact.message,
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const { error } = await supabase
        .from('contact_details')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Manage Contacts</h2>
        {/* Adding contacts from admin is not allowed */}
      </div>

      {showForm ? (
        <Card className="mb-8 bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">
              {isEditing ? 'Edit Contact' : 'Add New Contact'}
            </CardTitle>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium text-white">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Contact name"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-white">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Contact email"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="message" className="text-sm font-medium text-white">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Contact message"
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button 
                type="button"
                variant="outline" 
                onClick={resetForm}
              >
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
                disabled // Disable the submit button
              >
                <Check className="mr-2 h-4 w-4" /> {isEditing ? 'Update' : 'Save'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      ) : null}

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-white">Loading contacts...</p>
        </div>
      ) : contacts.length > 0 ? (
        <Card className="bg-white/5 border-purple-500/20 backdrop-blur-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-white/5 border-b border-white/10">
                  <TableHead className="text-white">Name</TableHead>
                  <TableHead className="text-white">Email</TableHead>
                  <TableHead className="text-white">Message</TableHead>
                  <TableHead className="text-white">Date</TableHead>
                  <TableHead className="text-white w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id} className="hover:bg-white/5 border-b border-white/10">
                    <TableCell className="font-medium text-white">{contact.name}</TableCell>
                    <TableCell className="text-gray-300"><a href={`mailto:${contact.email}`} className="hover:text-purple-400 underline">{contact.email}</a></TableCell>
                    <TableCell className="text-gray-300">{contact.message.length > 50 ? contact.message.substring(0, 50) + '...' : contact.message}</TableCell>
                    <TableCell className="text-gray-300 whitespace-nowrap">{new Date(contact.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {/* Editing is not allowed */}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          disabled
                          className="text-white hover:text-purple-400 hover:bg-white/10 cursor-not-allowed"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(contact.id)}
                          className="text-white hover:text-red-400 hover:bg-white/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-12 bg-white/5 rounded-lg border border-purple-500/20">
          <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Contacts Found</h3>
          <p className="text-gray-400 mb-6">No one has contacted you yet.</p>
          {/* Adding contacts from admin is not allowed */}
        </div>
      )}
    </div>
  );
};

export default ContactAdmin;
